import { Component, computed, inject, model, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { Observable, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { AsyncPipe, CommonModule } from '@angular/common';
import { NgScrollbar } from 'ngx-scrollbar';
import { AddRemoveButtonsComponent } from '../../../../buttons/add-remove-buttons/add-remove-buttons.component';
import { FloatingInputComponent } from '../../../../inputs/floating-input/floating-input.component';
import { SimpleButtonComponent } from '../../../../buttons/simple-medium-button/simple-button.component';
import { TooltipDirective } from '@app/directives/tooltip.directive';
import {
  getEmptySchedulesItem,
  getEmptySpendEntrySOV,
} from '@app/store/prime-commitments/prime-commitments.constants';
import { MoneyPipe } from '@app/pipes/framework/money-short.pipe';
import { PrimeContractModel } from '@app/store/prime-commitments/prime-commitments.types';
import { CommitmentEntryTextComponent } from '@app/framework/commitments/sidebar/commitment-entry-text/commitment-entry-text.component';
import { selectLineItemsExtended } from '@app/store/spend/spend.selectors';
import { primeContractActions } from '@app/store/prime-commitments/prime-commitments.actions';
import { NotificationsService } from '@app/services/notifications.service';
import { primeSelectors } from '@app/store/prime-commitments/prime-commitments.selectors';
import { LineItemSummary } from '@app/services/project-spend.types';
import {
  DropdownComponent,
  DropdownOption,
} from '@app/framework/inputs/dropdown/dropdown.component';
import { SidebarHeaderComponent } from '@app/framework/interaction-bar/sidebar-header/sidebar-header.component';

@Component({
  selector: 'app-prime-contract-sovs',
  imports: [
    AddRemoveButtonsComponent,
    FloatingInputComponent,
    FormsModule,
    AsyncPipe,
    SimpleButtonComponent,
    MoneyPipe,
    NgScrollbar,
    CommitmentEntryTextComponent,
    TooltipDirective,
    DropdownComponent,
    CommonModule,
    SidebarHeaderComponent,
  ],
  templateUrl: './prime-contract-sovs.component.html',
  styleUrl: './prime-contract-sovs.component.scss',
})
export class PrimeContractSOVsComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly notif = inject(NotificationsService);

  @ViewChild('spendEntriesPage') primeValuesForm: NgForm;

  model = model<PrimeContractModel>();
  // @Output() modelChange = new EventEmitter<IPrimeContractModel>();

  allLineItemsAsDropdown$: Observable<DropdownOption<LineItemSummary>[]> = this.store.select(
    primeSelectors.selectPrimeContractLineItemsAsDropdown,
  );

  allLineItems$ = this.store.select(selectLineItemsExtended);

  disabledLineItemIds = signal(new Set<number>());

  totals = computed(() => {
    if (!this.model()?.sovs || !Array.isArray(this.model().sovs)) {
      return 0;
    }
    return this.model().sovs.reduce((acc, sov) => {
      return (
        Number(acc) +
        sov.scheduled_items.reduce((acc, item) => {
          return Number(acc) + Number(item.value);
        }, 0)
      );
    }, 0);
  });

  sovTotal = computed((): number[] => {
    const totals: number[] = [];
    for (const sov of this.model().sovs) {
      if (!Array.isArray(sov?.scheduled_items)) {
        totals.push(0);
        continue;
      }

      const total = sov.scheduled_items.reduce((acc, item) => {
        return Number(acc) + Number(item?.value ?? 0);
      }, 0);
      totals.push(total);
    }

    return totals;
  });

  ngOnInit() {
    this.updateDisabledLineItemIds();
  }

  isFormValid() {
    this.primeValuesForm.form.markAllAsTouched();
    const invalidSOVs = this.model().sovs.filter((sov) => {
      return !sov.scheduled_items.length;
    });

    if (invalidSOVs.length > 0) {
      this.notif.showError('Please ensure all SOVs have at least one scheduled item');
      return false;
    }

    return this.primeValuesForm.form.valid;
  }

  goToNextPage() {
    if (!this.isFormValid()) {
      return;
    }

    this.store.dispatch(primeContractActions.onPrimeContractClickNext());
  }

  /**
   * Creates new SOVs based on the budget.
   * A parent line becomes a new SOV, and each subitem becomes a scheduled item beneath it.
   */
  createSOVsFromBudget() {
    this.allLineItems$.pipe(take(1)).subscribe((lineItems) => {
      this.model.update((currentValue) => {
        const sovs = lineItems
          .map((item) => ({
            ...item,
            subitems: item?.subitems?.filter((subitem) => !!subitem.original_budget_total) ?? [],
          }))
          .filter((item) => item?.subitems?.length > 0)
          .map((item, index) => {
            return {
              ...getEmptySpendEntrySOV(),
              name: item.name,
              index,
              scheduled_items: item.subitems.map((subitem, subitemIndex) => ({
                ...getEmptySchedulesItem(),
                value: subitem.original_budget_total,
                item_id: subitem.id,
                index: subitemIndex,
              })),
            };
          });

        if (sovs.length === 0) {
          this.notif.showError(
            'There are no line items with a budget that can be used to create SOVs.',
          );
          return currentValue;
        }

        return { ...currentValue, sovs };
      });
    });
  }
  onSOVNameChange(name: string, sovIndex: number) {
    this.model.update((currentValue) => {
      currentValue.sovs[sovIndex].name = name;
      return { ...currentValue };
    });
  }

  onLineItemChange(id: number, sovIndex: number, scheduledItemIndex: number) {
    this.model.update((currentValue) => {
      currentValue.sovs[sovIndex].scheduled_items[scheduledItemIndex].item_id = id;
      return { ...currentValue };
    });
    this.updateDisabledLineItemIds();
  }

  onLineItemValueChange(value: string, sovIndex: number, scheduledItemIndex: number) {
    this.model.update((currentValue) => {
      currentValue.sovs[sovIndex].scheduled_items[scheduledItemIndex].value = Number(value);
      return { ...currentValue };
    });
  }

  addEmptySpendEntrySOV() {
    this.model.update((currentValue) => {
      currentValue.sovs.push(getEmptySpendEntrySOV());
      return { ...currentValue };
    });
    this.setSpendEntriesIndex();
  }

  removeScheduledItem(spendEntryIndex: number, scheduledItemIndex: number) {
    if (this.model().sovs[spendEntryIndex].scheduled_items.length === 1) {
      return;
    }

    this.model.update((currentValue) => {
      currentValue.sovs[spendEntryIndex].scheduled_items.splice(scheduledItemIndex, 1);
      return { ...currentValue };
    });

    this.setSpendDescriptionIndex(spendEntryIndex);
    this.updateDisabledLineItemIds();
  }

  addScheduledItem(spendEntryIndex: number, scheduledItemIndex: number) {
    this.model.update((currentValue) => {
      currentValue.sovs[spendEntryIndex].scheduled_items.splice(scheduledItemIndex + 1, 0, {
        ...getEmptySchedulesItem(),
        index: scheduledItemIndex + 1,
      });
      return { ...currentValue };
    });

    this.setSpendDescriptionIndex(spendEntryIndex);
    this.updateDisabledLineItemIds();
  }

  setSpendDescriptionIndex(spendEntryIndex: number) {
    this.model.update((currentValue) => {
      currentValue.sovs[spendEntryIndex].scheduled_items.map((item, i) => {
        return {
          ...item,
          index: i,
        };
      });
      return { ...currentValue };
    });
  }

  removeSpendEntrySOV(index: number) {
    this.model.update((currentValue) => {
      currentValue.sovs.splice(index, 1);
      return { ...currentValue };
    });

    this.setSpendEntriesIndex();
    this.updateDisabledLineItemIds();
  }

  setSpendEntriesIndex() {
    this.model.update((currentValue) => {
      currentValue.sovs = currentValue.sovs.map((item, i) => {
        return {
          ...item,
          index: i,
        };
      });
      return { ...currentValue };
    });
  }

  goToPreviousPage() {
    this.store.dispatch(primeContractActions.onPrimeContactPreviousPage());
  }

  updateDisabledLineItemIds() {
    if (!this.model()?.sovs || !Array.isArray(this.model().sovs)) {
      this.disabledLineItemIds.set(new Set<number>());
      return;
    }

    const newDisabledIds = this.model()
      .sovs.flatMap((sov) => {
        return sov.scheduled_items.map((item) => {
          return item.item_id;
        });
      })
      .filter((id) => !!id);
    this.disabledLineItemIds.set(new Set(newDisabledIds));
  }
}
